import { useEffect, useState, useCallback, Fragment } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, FloppyDisk, Plus, Trash, ArrowClockwise, Camera } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PLAYER = () => ({
  name: '',
  misto: [null, null, null, null, null],
  genero: [null, null, null, null, null],
  rei_rainha: [null, null, null, null, null]
});

function computeTotal(player) {
  const sum = (arr) => (arr || []).reduce((acc, v) => acc + (v == null || v === '' ? 0 : Number(v) || 0), 0);
  return sum(player.misto) + sum(player.genero) + sum(player.rei_rainha);
}

function RankingEditor({ gender, label, crownLabel }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/league/rankings`, {
        params: { gender },
        withCredentials: true
      });
      const list = data.players || [];
      // Sort by total desc (name tiebreak) so admin sees same order as public/portal pages.
      list.sort((a, b) => {
        const diff = computeTotal(b) - computeTotal(a);
        if (diff !== 0) return diff;
        return (a.name || '').localeCompare(b.name || '', 'pt-BR');
      });
      setPlayers(list);
      setSnapshotAt(data.snapshot_at || null);
    } catch {
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  }, [gender]);

  useEffect(() => { load(); }, [load]);

  const updateCell = (idx, group, etapaIdx, value) => {
    setPlayers(prev => {
      const next = [...prev];
      const p = { ...next[idx], [group]: [...next[idx][group]] };
      p[group][etapaIdx] = value === '' ? null : Number(value);
      next[idx] = p;
      return next;
    });
  };

  const updateName = (idx, value) => {
    setPlayers(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], name: value };
      return next;
    });
  };

  const addPlayer = () => setPlayers(prev => [...prev, EMPTY_PLAYER()]);

  const removePlayer = (idx) => {
    if (!window.confirm('Remover este atleta do ranking?')) return;
    setPlayers(prev => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    const cleanPlayers = players.filter(p => (p.name || '').trim());
    if (cleanPlayers.length !== players.length) {
      toast.warning('Atletas sem nome foram removidos');
    }
    setSaving(true);
    try {
      await axios.put(`${API}/league/rankings`, { gender, players: cleanPlayers }, { withCredentials: true });
      toast.success('Ranking salvo!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const snapshot = async () => {
    const msg = snapshotAt
      ? 'Ja existe um snapshot. Substituir pelo estado atual? (As setas de variacao da proxima etapa vao comparar com a ordem ATUAL.)'
      : 'Tirar um snapshot da ordem atual? Isso vai congelar as posicoes para que as setas de variacao apareçam apos a proxima etapa.';
    if (!window.confirm(msg)) return;
    setSnapping(true);
    try {
      await axios.post(`${API}/league/rankings/snapshot`, { gender }, { withCredentials: true });
      toast.success('Snapshot da etapa salvo!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar snapshot');
    } finally {
      setSnapping(false);
    }
  };

  // indexed rows so we can render but filtered visually
  const visible = players
    .map((p, i) => ({ ...p, _idx: i }))
    .filter(p => !filter || p.name.toLowerCase().includes(filter.toLowerCase()));

  const cellClass = 'w-14 h-9 px-2 text-center text-sm border border-[#E6E2D8] rounded focus:border-[#E85D04] focus:outline-none';

  return (
    <div className="space-y-4" data-testid={`ranking-editor-${gender}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filtrar por nome..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
          data-testid={`ranking-filter-${gender}`}
        />
        <Button variant="outline" onClick={load} disabled={loading} data-testid={`ranking-reload-${gender}`}>
          <ArrowClockwise size={16} className="mr-2" /> Recarregar
        </Button>
        <Button variant="outline" onClick={addPlayer} data-testid={`ranking-add-${gender}`}>
          <Plus size={16} className="mr-2" /> Adicionar atleta
        </Button>
        <Button
          variant="outline"
          onClick={snapshot}
          disabled={snapping}
          className="border-[#FFB703] text-[#7A5400] hover:bg-[#FFD60A]/20"
          data-testid={`ranking-snapshot-${gender}`}
          title="Congela a ordem atual para gerar setas de variacao na proxima etapa"
        >
          {snapping ? <div className="w-4 h-4 border-2 border-[#7A5400] border-t-transparent rounded-full animate-spin mr-2" /> : <Camera size={16} className="mr-2" />}
          Snapshot Etapa
        </Button>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-[#73706A]">{players.length} atletas{snapshotAt ? ` - snapshot: ${new Date(snapshotAt).toLocaleDateString('pt-BR')}` : ''}</span>
          <Button onClick={save} disabled={saving} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid={`ranking-save-${gender}`}>
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
            Salvar {label}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#F5F2EB] text-[#73706A] uppercase text-xs sticky top-0 z-10">
            <tr>
              <th rowSpan={2} className="px-3 py-3 text-left align-middle border-r border-[#E6E2D8]">#</th>
              <th rowSpan={2} className="px-3 py-3 text-left min-w-[180px] align-middle border-r border-[#E6E2D8]">Atleta</th>
              {[1, 2, 3, 4, 5].map(n => (
                <th key={`e${n}`} colSpan={3} className="px-2 py-2 text-center border-r border-[#E6E2D8] bg-[#EEE9DC] font-bold">E{n}</th>
              ))}
              <th rowSpan={2} className="px-3 py-3 text-right align-middle border-l border-[#E6E2D8]">Total</th>
              <th rowSpan={2} className="px-2 py-3 align-middle" />
            </tr>
            <tr>
              {[1, 2, 3, 4, 5].map(n => (
                <Fragment key={`sub${n}`}>
                  <th className="px-2 py-1 text-center font-medium">Misto</th>
                  <th className="px-2 py-1 text-center font-medium">{label}</th>
                  <th className="px-2 py-1 text-center font-medium border-r border-[#E6E2D8]">{crownLabel}</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={19} className="p-8 text-center text-[#73706A]">Carregando...</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={19} className="p-8 text-center text-[#73706A]">Nenhum atleta</td></tr>
            ) : visible.map((p) => (
              <tr key={p._idx} className="border-t border-[#E6E2D8] hover:bg-[#FDFBF7]" data-testid={`ranking-row-${gender}-${p._idx}`}>
                <td className="px-3 py-2 text-[#73706A]">{p._idx + 1}</td>
                <td className="px-3 py-2">
                  <Input
                    value={p.name}
                    onChange={(e) => updateName(p._idx, e.target.value)}
                    className="h-9"
                    data-testid={`ranking-name-${gender}-${p._idx}`}
                  />
                </td>
                {[0, 1, 2, 3, 4].map(ei => (
                  <Fragment key={`e${ei}`}>
                    <td className="px-1 py-2 text-center">
                      <input
                        type="number"
                        value={p.misto[ei] == null ? '' : p.misto[ei]}
                        onChange={(e) => updateCell(p._idx, 'misto', ei, e.target.value)}
                        className={cellClass}
                      />
                    </td>
                    <td className="px-1 py-2 text-center">
                      <input
                        type="number"
                        value={p.genero[ei] == null ? '' : p.genero[ei]}
                        onChange={(e) => updateCell(p._idx, 'genero', ei, e.target.value)}
                        className={cellClass}
                      />
                    </td>
                    <td className="px-1 py-2 text-center border-r border-[#E6E2D8]">
                      <input
                        type="number"
                        value={p.rei_rainha[ei] == null ? '' : p.rei_rainha[ei]}
                        onChange={(e) => updateCell(p._idx, 'rei_rainha', ei, e.target.value)}
                        className={cellClass}
                      />
                    </td>
                  </Fragment>
                ))}
                <td className="px-3 py-2 text-right font-['Outfit'] font-bold text-[#E85D04] border-l border-[#E6E2D8]">{computeTotal(p)}</td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => removePlayer(p._idx)}
                    className="text-[#9B2226] hover:bg-red-50 p-1 rounded"
                    title="Remover"
                    data-testid={`ranking-delete-${gender}-${p._idx}`}
                  >
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#73706A]">
        Dica: deixe a celula em branco para etapa nao disputada. A tabela e reordenada automaticamente por Total ao Salvar ou Recarregar.<br/>
        <strong>Fluxo da etapa:</strong> 1) Tire um <em>Snapshot Etapa</em> ANTES de digitar os novos pontos &middot; 2) Digite os pontos da nova etapa &middot; 3) Salve. As setas de variacao (subiu/caiu) aparecem automaticamente nas paginas publicas e portal do aluno.
      </p>
    </div>
  );
}

export default function RankingsAdmin() {
  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="rankings-admin-page">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Trophy size={28} className="text-[#E85D04]" weight="fill" /> Rankings TK League
          </h1>
          <p className="text-[#73706A] mt-1">Edite os pontos de cada etapa. Os rankings sao atualizados automaticamente na pagina publica /tk-league.</p>
        </div>

        <Tabs defaultValue="feminino">
          <TabsList className="bg-[#F5F2EB]">
            <TabsTrigger value="feminino" className="px-6" data-testid="admin-tab-feminino">Feminino</TabsTrigger>
            <TabsTrigger value="masculino" className="px-6" data-testid="admin-tab-masculino">Masculino</TabsTrigger>
          </TabsList>

          <TabsContent value="feminino" className="mt-4">
            <RankingEditor gender="feminino" label="Feminino" crownLabel="Rainha" />
          </TabsContent>
          <TabsContent value="masculino" className="mt-4">
            <RankingEditor gender="masculino" label="Masculino" crownLabel="Rei" />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
